import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { trackerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TIME_FILTERS = [
  { label: 'Past Day', value: 'day' },
  { label: 'Past Week', value: 'week' },
  { label: 'Past Month', value: 'month' },
  { label: 'Past 3 Months', value: '3 months' },
  { label: 'Past 6 Months', value: '6 months' },
  { label: '2026', value: '2026' },
  { label: 'All Time', value: 'all time' },
];

const STATUSES = ['saved', 'applied', 'interviewing', 'offer', 'rejected'];

const FUNNEL_THEMES = {
  all: {
    color: 'var(--color-charcoal-900)',
    bg: 'rgba(15, 23, 42, 0.04)',
    border: 'var(--color-border)',
    activeBg: 'rgba(15, 23, 42, 0.08)',
    activeBorder: 'var(--color-charcoal-800)',
    glow: 'var(--glow-accent)'
  },
  saved: {
    color: 'var(--color-charcoal-700)',
    bg: 'rgba(15, 23, 42, 0.02)',
    border: 'var(--color-border)',
    activeBg: 'rgba(15, 23, 42, 0.06)',
    activeBorder: 'var(--color-charcoal-700)',
    glow: 'var(--glow-accent)'
  },
  applied: {
    color: 'var(--color-sky-600)',
    bg: 'rgba(2, 132, 199, 0.02)',
    border: 'rgba(2, 132, 199, 0.12)',
    activeBg: 'rgba(2, 132, 199, 0.08)',
    activeBorder: 'var(--color-sky-600)',
    glow: '0 0 0 3px rgba(2, 132, 199, 0.16)'
  },
  interviewing: {
    color: 'var(--color-purple-600)',
    bg: 'rgba(124, 58, 237, 0.02)',
    border: 'rgba(124, 58, 237, 0.12)',
    activeBg: 'rgba(124, 58, 237, 0.08)',
    activeBorder: 'var(--color-purple-600)',
    glow: '0 0 0 3px rgba(124, 58, 237, 0.16)'
  },
  offer: {
    color: 'var(--color-success)',
    bg: 'var(--color-success-bg)',
    border: 'var(--color-success-border)',
    activeBg: 'rgba(22, 163, 74, 0.08)',
    activeBorder: 'var(--color-success)',
    glow: 'var(--glow-success)'
  },
  rejected: {
    color: 'var(--color-error)',
    bg: 'var(--color-error-bg)',
    border: 'var(--color-error-border)',
    activeBg: 'rgba(220, 38, 38, 0.08)',
    activeBorder: 'var(--color-error)',
    glow: 'var(--glow-error)'
  }
};

const JDModal = ({ isOpen, onClose, company, title, jdText }) => {
  if (!isOpen) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={{ margin: 0, fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)' }}>Job Description</h3>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>
        <div style={styles.modalSubHeader}>{title} @ <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{company}</span></div>
        <div style={styles.modalBody}>
          {jdText}
        </div>
      </div>
    </div>
  );
};

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px', marginTop: '-2px', verticalAlign: 'middle'}}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const HistoryCard = ({ item, handleStatusUpdate, handleDelete, handleDownload, handleDownloadPdf, downloadingJobId, downloadedJobIds, updateItemInState, statusActionsRef, showConfetti }) => {
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editCompany, setEditCompany] = useState(item.company_name);
  const [editJobTitle, setEditJobTitle] = useState(item.job_title);
  
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState(item.notes || '');
  const [isHovered, setIsHovered] = useState(false);
  
  const [showJD, setShowJD] = useState(false);

  const currentStatus = item.pipeline_status || 'saved';

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleSaveDetails = async () => {
    try {
      await trackerAPI.updateSnapshot(item.snapshot_id, { 
        company_name: editCompany, 
        job_title: editJobTitle 
      });
      updateItemInState(item.snapshot_id, { company_name: editCompany, job_title: editJobTitle });
      setIsEditingDetails(false);
    } catch (err) {
      alert('Failed to update details.');
    }
  };

  const handleSaveNotes = async () => {
    try {
      await trackerAPI.updateSnapshot(item.snapshot_id, { notes: editNotes });
      updateItemInState(item.snapshot_id, { notes: editNotes });
      setIsEditingNotes(false);
    } catch (err) {
      alert('Failed to update notes.');
    }
  };

  const scoreColor = item.match_score >= 80 ? 'var(--color-success)' : item.match_score >= 60 ? 'var(--color-warning)' : 'var(--color-error)';
  const scoreBgColor = item.match_score >= 80 ? 'var(--color-success-bg)' : item.match_score >= 60 ? 'var(--color-warning-bg)' : 'var(--color-error-bg)';
  const scoreBorderColor = item.match_score >= 80 ? 'var(--color-success-border)' : item.match_score >= 60 ? 'var(--color-warning-border)' : 'var(--color-error-border)';

  return (
    <div 
      className={`history-card ${item.snapshot_id === 'sample-snapshot-id' ? 'tour-active-container' : ''}`}
      style={styles.card}
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.cardHeader}>
        <div style={styles.cardHeaderLeft}>
          {isEditingDetails ? (
            <div style={styles.editForm}>
              <input value={editCompany} onChange={e => setEditCompany(e.target.value)} placeholder="Company Name" style={styles.inlineInput} />
              <input value={editJobTitle} onChange={e => setEditJobTitle(e.target.value)} placeholder="Job Title" style={styles.inlineInput} />
              <div style={styles.editActions}>
                <button onClick={handleSaveDetails} style={styles.saveBtn}>Save</button>
                <button onClick={() => setIsEditingDetails(false)} style={styles.cancelBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={styles.detailsDisplay} title="Click to edit details">
              <div style={styles.companyTitleWrapper} onClick={() => setIsEditingDetails(true)}>
                <h3 style={styles.companyName}>{item.company_name}</h3>
                <span style={styles.editIcon}>✎</span>
              </div>
              <div style={styles.jobTitle} onClick={() => setIsEditingDetails(true)}>{item.job_title}</div>
            </div>
          )}
          
          <div style={styles.metaRow}>
            <span style={styles.dateText}>Saved {formatDate(item.created_at)}</span>
            {item.job_description_text && (
              <>
                <span style={styles.dotSeparator}>•</span>
                <span style={styles.viewJdLink} onClick={() => setShowJD(true)}>📄 View JD</span>
                <JDModal 
                  isOpen={showJD} 
                  onClose={() => setShowJD(false)}
                  company={item.company_name}
                  title={item.job_title}
                  jdText={item.job_description_text}
                />
              </>
            )}
          </div>
        </div>
        
        <div style={styles.cardHeaderRight}>
          {item.optimized_score != null ? (
            <div style={styles.scoreComparisonWrapper}>
              <div style={styles.initialScoreBadge} title="Initial Analysis Match Score">
                <span style={{fontSize: '16px', fontWeight: 800}}>{Math.round(item.match_score || 0)}%</span>
                <span style={{fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Analysis</span>
              </div>
              <div style={styles.scoreArrowBox} title={`+${Math.max(0, Math.round(item.optimized_score - (item.match_score || 0)))}% Improvement`}>
                ➔
              </div>
              <div style={styles.optimizedScoreBadge} title="AI Optimized Match Score">
                <span style={{fontSize: '16px', fontWeight: 800}}>{Math.round(item.optimized_score)}%</span>
                <span style={{fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Optimized</span>
              </div>
            </div>
          ) : (
            <div style={{...styles.scoreBadge, color: scoreColor, backgroundColor: scoreBgColor, borderColor: scoreBorderColor}}>
              <span style={{fontSize: '18px'}}>{item.match_score}%</span> Match
            </div>
          )}
          <button onClick={() => handleDelete(item.snapshot_id)} style={styles.deleteIconBtn} title="Delete Application">
            <TrashIcon />
          </button>
        </div>
      </div>

      <div className="history-card-details-responsive" style={styles.cardBody}>
        {/* Pipeline Actions */}
        <div style={styles.pipelineSection}>
          <div style={styles.sectionLabel}>Pipeline Status</div>
          <div
            ref={statusActionsRef}
            className={item.snapshot_id === 'sample-snapshot-id' ? 'tour-highlight-pulse' : ''}
            style={{ ...styles.statusActions, position: 'relative' }}
          >
            {currentStatus === 'saved' && (
              <button onClick={() => handleStatusUpdate(item.snapshot_id, 'applied')} style={{...styles.actionBtn, ...styles.applyBtn}}>🚀 Mark as Applied</button>
            )}
            {currentStatus === 'applied' && (
              <>
                <button onClick={() => handleStatusUpdate(item.snapshot_id, 'interviewing')} style={{...styles.actionBtn, ...styles.interviewBtn}}>💬 Got Interview</button>
                <button onClick={() => handleStatusUpdate(item.snapshot_id, 'rejected')} style={{...styles.actionBtn, ...styles.rejectBtn}}>Rejected</button>
              </>
            )}
            {currentStatus === 'interviewing' && (
              <>
                <button onClick={() => handleStatusUpdate(item.snapshot_id, 'offer')} style={{...styles.actionBtn, ...styles.offerBtn}}>🎉 Got Offer!</button>
                <button onClick={() => handleStatusUpdate(item.snapshot_id, 'rejected')} style={{...styles.actionBtn, ...styles.rejectBtn}}>Rejected</button>
              </>
            )}
            {(currentStatus === 'offer' || currentStatus === 'rejected' || currentStatus === 'saved') && (
              <span style={{...styles.statusLabel, ...(currentStatus === 'offer' ? styles.statusLabelOffer : currentStatus === 'rejected' ? styles.statusLabelReject : {})}}>
                {currentStatus.toUpperCase()}
              </span>
            )}
            {showConfetti && (
              <div className="tour-confetti-burst">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className={`confetti-piece p${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={styles.verticalDivider}></div>

        {/* Artifacts (Downloads) */}
        <div style={styles.artifactsSection}>
          <div style={styles.sectionLabel}>Optimized Resumes</div>
          <div style={styles.artifactsList}>
            {item.artifacts_metadata && item.artifacts_metadata.length > 0 ? (
              item.artifacts_metadata
                .filter(artifact => !['PDF', 'JSON'].includes(String(artifact.kind || '').toUpperCase()))
                .map((artifact, index) => {
                  const normalizedKind = String(artifact.kind || 'file').toLowerCase();
                  const label = normalizedKind === 'docx' ? 'Download as Docx' : `Download ${normalizedKind.toUpperCase()}`;
                  return (
                    <button key={index} onClick={() => handleDownload(artifact.artifact_id)} style={{...styles.downloadBtn, ...styles.downloadDoc}}>
                      <DownloadIcon /> {label}
                    </button>
                  );
                })
            ) : null}
            
            {/* On-Demand PDF Download Button */}
            {item.optimization_job_id && (
              (() => {
                const isConverting = downloadingJobId === item.optimization_job_id;
                const isDone = downloadedJobIds && downloadedJobIds.has(item.optimization_job_id);
                return (
                  <button 
                    onClick={() => {
                      const docxArtifact = item.artifacts_metadata?.find(a => String(a.kind).toLowerCase() === 'docx');
                      handleDownloadPdf(item.optimization_job_id, docxArtifact?.file_name);
                    }} 
                    disabled={isConverting}
                    style={{
                      ...styles.downloadBtn,
                      ...styles.downloadPdf,
                      opacity: isConverting ? 0.75 : 1,
                      cursor: isConverting ? 'not-allowed' : 'pointer',
                      minWidth: '148px',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    {isConverting ? (
                      <>
                        <span style={styles.pdfBtnSpinner} />
                        &nbsp;Converting…
                      </>
                    ) : isDone ? (
                      <><DownloadIcon /> Download PDF ✓</>
                    ) : (
                      <><DownloadIcon /> Download as PDF</>
                    )}
                  </button>
                );
              })()
            )}
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div style={styles.notesContainer}>
        {Array.isArray(item.claimed_skills_used) && item.claimed_skills_used.length > 0 && (
          <div style={styles.claimedSkillsBlock}>
            <div style={styles.noteTitle}>Claimed Skill Trace</div>
            {item.claimed_skills_summary && (
              <div style={styles.claimedSkillsSummary}>
                Requested: {item.claimed_skills_summary.requested ?? 0} | Applied: {item.claimed_skills_summary.applied ?? 0} | Not Applied: {item.claimed_skills_summary.not_applied ?? 0}
              </div>
            )}
            <div style={styles.claimedSkillsList}>
              {item.claimed_skills_used.slice(0, 3).map((trace, idx) => {
                const outputLocations = Array.isArray(trace?.output_locations) ? trace.output_locations.filter(Boolean) : [];
                return (
                  <div key={`${item.snapshot_id}-trace-${idx}`} style={styles.claimedSkillItem}>
                    <div style={styles.claimedSkillHeader}>
                      <strong>{trace?.skill || 'Skill'}</strong> - {trace?.applied ? 'Applied' : 'Not Applied'}
                    </div>
                    {trace?.reason ? <div style={styles.claimedSkillMeta}>{trace.reason}</div> : null}
                    {outputLocations.length > 0 ? (
                      <div style={styles.claimedSkillMeta}>Output locations: {outputLocations.join(', ')}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isEditingNotes ? (
          <div style={styles.editForm}>
            <textarea 
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="e.g. Interview with Jane Doe on Friday..."
              style={styles.notesInputActive}
              autoFocus
            />
            <div style={styles.editActions}>
              <button onClick={handleSaveNotes} style={styles.saveBtn}>Save Note</button>
              <button onClick={() => setIsEditingNotes(false)} style={styles.cancelBtn}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {item.notes ? (
              <div style={styles.notesDisplay} onClick={() => setIsEditingNotes(true)}>
                <div style={styles.noteTitle}>Personal Notes <span style={styles.editIcon}>✎</span></div>
                {item.notes}
              </div>
            ) : (
              <div style={{ opacity: isHovered ? 1 : 0, transition: 'opacity 0.2s', height: isHovered ? 'auto' : '0', overflow: 'hidden' }}>
                <button style={styles.addNoteBtn} onClick={() => setIsEditingNotes(true)}>+ Add a personal note</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const HistoryTable = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [downloadedJobIds, setDownloadedJobIds] = useState(new Set());
  
  const [timeFilter, setTimeFilter] = useState('6 months');
  const [statusFilter, setStatusFilter] = useState('all');

  // History Onboarding Tour State
  const [tourStep, setTourStep] = useState(null); // null or 1
  const [showConfetti, setShowConfetti] = useState(false);
  const statusActionsRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await trackerAPI.getHistory();
      let data = response.data || [];
      const userTourKey = `hasSeenHistoryTour_${user?.email || 'guest'}`;
      const hasSeenTour = localStorage.getItem(userTourKey) === 'true';
      if (!hasSeenTour) {
        const sampleCard = {
          snapshot_id: 'sample-snapshot-id',
          company_name: 'Google',
          job_title: 'Senior Software Engineer',
          created_at: new Date().toISOString(),
          pipeline_status: 'saved',
          match_score: 42,
          optimized_score: 95,
          job_description_text: 'This is a sample job description representing your target role.',
          notes: 'Try updating the status below to see how easy it is to manage your applications!',
          artifacts_metadata: [
            { kind: 'docx', artifact_id: 'sample-doc' }
          ],
          isSample: true
        };
        data = [sampleCard, ...data];
        setTourStep(1);
      }
      setHistory(data);
      setError(null);
    } catch (err) {
      setError('Failed to load application history.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Scroll to sample card status buttons when the tour starts
  useEffect(() => {
    if (tourStep === 1 && !loading) {
      const timer = setTimeout(() => {
        if (statusActionsRef.current) {
          const rect = statusActionsRef.current.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - (window.innerHeight / 2) + (rect.height / 2);
          window.scrollTo({
            top: targetY,
            behavior: 'smooth'
          });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [tourStep, loading]);

  const handleDelete = async (snapshotId) => {
    if (snapshotId === 'sample-snapshot-id') return;
    if (!window.confirm('Are you sure you want to delete this tracked application?')) return;
    try {
      await trackerAPI.deleteSnapshot(snapshotId);
      fetchHistory();
    } catch (err) {
      alert('Failed to delete snapshot.');
    }
  };

  const handleStatusUpdate = async (snapshotId, newStatus) => {
    if (snapshotId === 'sample-snapshot-id') {
      updateItemInState(snapshotId, { pipeline_status: newStatus });
      setShowConfetti(true);
      if (newStatus === 'applied') {
        setTimeout(() => {
          setShowConfetti(false);
          setTourStep(2);
        }, 2000);
      } else if (newStatus === 'interviewing') {
        setTimeout(() => {
          setShowConfetti(false);
          setTourStep(3);
        }, 2000);
      } else {
        setTimeout(() => {
          setShowConfetti(false);
          setTourStep(4);
        }, 2000);
      }
      return;
    }
    try {
      updateItemInState(snapshotId, { pipeline_status: newStatus });
      await trackerAPI.updateSnapshot(snapshotId, { pipeline_status: newStatus });
    } catch (err) {
      alert('Failed to update status.');
      fetchHistory(); // revert on failure
    }
  };

  const handleDownload = async (artifactId) => {
    try {
      const response = await trackerAPI.getDownloadUrl(artifactId);
      if (response.data?.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        alert('Download URL not found.');
      }
    } catch (err) {
      alert('Failed to fetch download URL.');
    }
  };

  const handleDownloadPdf = async (jobId, docxName) => {
    if (!jobId || downloadingJobId === jobId) return;
    setDownloadingJobId(jobId);
    console.log('[PDF] Requesting PDF for job (history):', jobId);
    try {
      console.log('[PDF] Sending request to backend — DOCX→PDF conversion starting...');
      const response = await trackerAPI.downloadPdf(jobId);
      console.log('[PDF] Conversion complete. Received blob.');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = docxName
        ? docxName.replace(/\.docx$/i, '.pdf')
        : `optimized_resume_${jobId.slice(0, 8)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.download = filename;
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadedJobIds(prev => new Set([...prev, jobId]));
      console.log('[PDF] File download triggered successfully.');
    } catch (error) {
      console.error('[PDF] Failed to download PDF:', error);
      alert('Failed to generate and download PDF. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  };

  const updateItemInState = (snapshotId, updates) => {
    setHistory(prev => prev.map(item => item.snapshot_id === snapshotId ? { ...item, ...updates } : item));
  };

  // Filter history by time
  const timeFilteredHistory = useMemo(() => {
    const now = new Date();
    return history.filter(item => {
      const itemDate = new Date(item.created_at);
      switch (timeFilter) {
        case 'day': return (now - itemDate) <= 24 * 60 * 60 * 1000;
        case 'week': return (now - itemDate) <= 7 * 24 * 60 * 60 * 1000;
        case 'month': return (now - itemDate) <= 30 * 24 * 60 * 60 * 1000;
        case '3 months': return (now - itemDate) <= 90 * 24 * 60 * 60 * 1000;
        case '6 months': return (now - itemDate) <= 180 * 24 * 60 * 60 * 1000;
        case '2026': return itemDate.getFullYear() === 2026;
        case 'all time': default: return true;
      }
    });
  }, [history, timeFilter]);

  // Funnel counts
  const counts = useMemo(() => {
    const init = { all: timeFilteredHistory.length, saved: 0, applied: 0, interviewing: 0, offer: 0, rejected: 0 };
    timeFilteredHistory.forEach(item => {
      const status = item.pipeline_status || 'saved';
      if (init[status] !== undefined) init[status]++;
    });
    return init;
  }, [timeFilteredHistory]);

  // Filter by status
  const displayedHistory = useMemo(() => {
    if (statusFilter === 'all') return timeFilteredHistory;
    return timeFilteredHistory.filter(item => (item.pipeline_status || 'saved') === statusFilter);
  }, [timeFilteredHistory, statusFilter]);

  if (loading) return <div style={styles.loading}>Loading tracking history...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      {tourStep && (
        <>
          <div className="tour-backdrop-scrim" />
          <HistoryTourPopover
            step={tourStep}
            targetRef={statusActionsRef}
            onSkip={() => {
              setTourStep(null);
              const userTourKey = `hasSeenHistoryTour_${user?.email || 'guest'}`;
              localStorage.setItem(userTourKey, 'true');
              setHistory(prev => prev.filter(item => item.snapshot_id !== 'sample-snapshot-id'));
            }}
          />
        </>
      )}
      <div style={styles.headerArea}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: 'var(--text-3xl)', fontWeight: 800, letterSpacing: '-0.05em', color: 'var(--color-text-primary)', lineHeight: 1.1 }}>
            Job Tracker
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.45)', fontWeight: 400, letterSpacing: '0.01em' }}>
            Your application pipeline, every stage at a glance.
          </p>
        </div>
        <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)} style={styles.timeSelect}>
          {TIME_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {history.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>No applications tracked yet</h3>
          <p>Optimize a resume for a job and save it to the tracker to see it here!</p>
        </div>
      ) : (
        <>
          <div className="history-funnel-pills" style={styles.funnelHeader}>
            {['all', ...STATUSES].map(status => {
              const theme = FUNNEL_THEMES[status] || FUNNEL_THEMES.saved;
              const isActive = statusFilter === status;
              return (
                <div 
                  key={status} 
                  className="glass-tile"
                  style={{
                    ...styles.funnelCard,
                    borderColor: isActive ? theme.activeBorder : theme.border,
                    backgroundColor: isActive ? theme.activeBg : theme.bg,
                    boxShadow: isActive ? theme.glow : undefined,
                  }}
                  onClick={() => setStatusFilter(status)}
                >
                  <div className="history-funnel-pill-title" style={{ ...styles.funnelCardTitle, color: isActive ? theme.color : 'var(--color-text-muted)' }}>{status.toUpperCase()}</div>
                  <div style={{ ...styles.funnelCardCount, color: theme.color }}>{counts[status]}</div>
                </div>
              );
            })}
          </div>

          <div className="history-coverflow">
            {displayedHistory.length === 0 ? (
              <div style={styles.emptyFilter}>No applications found in this state.</div>
            ) : (
              displayedHistory.map((item) => (
                <HistoryCard 
                  key={item.snapshot_id}
                  item={item}
                  handleStatusUpdate={handleStatusUpdate}
                  handleDelete={handleDelete}
                  handleDownload={handleDownload}
                  handleDownloadPdf={handleDownloadPdf}
                  downloadingJobId={downloadingJobId}
                  downloadedJobIds={downloadedJobIds}
                  updateItemInState={updateItemInState}
                  statusActionsRef={item.snapshot_id === 'sample-snapshot-id' ? statusActionsRef : null}
                  showConfetti={item.snapshot_id === 'sample-snapshot-id' && showConfetti}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: { backgroundColor: 'transparent', padding: '8px 0', minHeight: '80vh', width: '100%' },
  headerArea: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '36px', gap: '20px' },
  timeSelect: {
    padding: '9px 16px', borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)', fontSize: 'var(--text-sm)',
    outline: 'none', cursor: 'pointer', backgroundColor: 'var(--color-surface)',
    boxShadow: 'var(--shadow-xs)', color: 'var(--color-text-primary)',
    fontWeight: 'var(--font-weight-medium)',
    transition: 'all var(--transition-fast)',
  },

  funnelHeader: { display: 'flex', gap: '10px', marginBottom: '36px', overflowX: 'auto', paddingBottom: '8px', flexWrap: 'nowrap' },
  funnelCard: {
    flex: '1', minWidth: '88px',
    padding: '14px 12px', cursor: 'pointer',
    textAlign: 'center', border: '1px solid var(--color-border)',
    transition: 'all var(--transition-base)',
    userSelect: 'none',
    borderRadius: 'var(--radius-lg)',
  },
  funnelCardTitle: {
    fontSize: '13px',
    fontWeight: 800, marginBottom: '8px', letterSpacing: '0.1em', textTransform: 'uppercase',
    transition: 'color var(--transition-base)',
  },
  funnelCardCount: { fontSize: '26px', fontWeight: 800, fontFamily: 'var(--font-heading)', transition: 'color var(--transition-base)', lineHeight: 1 },

  loading: { padding: '60px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-base)' },
  error: { padding: '20px', color: 'var(--color-error)', backgroundColor: 'var(--color-error-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-error-border)' },
  emptyState: {
    padding: '60px 20px', textAlign: 'center', backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-xl)', border: '1.5px dashed var(--color-border-strong)',
    color: 'var(--color-text-secondary)',
  },
  emptyFilter: { padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-base)', width: '100%' },

  card: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  cardHeaderLeft: { flex: 1, minWidth: 0 },
  cardHeaderRight: { display: 'flex', alignItems: 'flex-start', gap: '14px', flexShrink: 0 },

  detailsDisplay: { cursor: 'pointer', borderRadius: 'var(--radius-sm)', width: '100%' },
  companyTitleWrapper: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%' },
  companyName: { fontWeight: 800, fontSize: 'var(--text-lg)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jobTitle: { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginTop: '4px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  editIcon: { fontSize: '13px', color: 'var(--color-text-muted)', opacity: 0.6 },

  metaRow: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' },
  dateText: { fontSize: '14px', color: 'var(--color-text-muted)', fontWeight: 500, letterSpacing: '0.01em' },
  dotSeparator: { fontSize: '14px', color: 'var(--color-text-muted)' },
  viewJdLink: {
    fontSize: '12px', color: 'var(--color-text-primary)', cursor: 'pointer',
    fontWeight: 700, padding: '3px 10px',
    backgroundColor: 'var(--color-surface-dark)', borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    transition: 'all var(--transition-fast)',
  },

  scoreBadge: {
    padding: '6px 12px', borderRadius: 'var(--radius-full)',
    fontWeight: 700, fontSize: '14px', border: '1px solid',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', lineHeight: 1.2,
    fontFamily: 'var(--font-heading)',
    flexShrink: 0,
  },
  scoreComparisonWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  initialScoreBadge: {
    padding: '6px 10px',
    borderRadius: 'var(--radius-full)',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid var(--color-error-border, rgba(239, 68, 68, 0.4))',
    backgroundColor: 'var(--color-error-bg, rgba(239, 68, 68, 0.1))',
    color: 'var(--color-error, #ef4444)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.2,
    fontFamily: 'var(--font-heading)',
    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.08)',
  },
  optimizedScoreBadge: {
    padding: '6px 10px',
    borderRadius: 'var(--radius-full)',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid var(--color-success-border, rgba(16, 185, 129, 0.4))',
    backgroundColor: 'var(--color-success-bg, rgba(16, 185, 129, 0.12))',
    color: 'var(--color-success, #10b981)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.2,
    fontFamily: 'var(--font-heading)',
    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.12)',
  },
  scoreArrowBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-secondary)',
    fontSize: '14px',
    fontWeight: 800,
    padding: '0 1px',
  },
  deleteIconBtn: {
    background: 'none', border: 'none', color: 'var(--color-text-muted)',
    cursor: 'pointer', padding: '4px', borderRadius: 'var(--radius-sm)',
    transition: 'all var(--transition-fast)',
  },

  cardBody: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start',
    backgroundColor: 'var(--color-surface-dark)',
    padding: '16px 20px', borderRadius: 'var(--radius-lg)', marginTop: '16px',
    border: '1px solid var(--color-border)',
    gap: '0',
  },
  pipelineSection: { flex: 1, paddingRight: '20px', minWidth: 0 },
  artifactsSection: { flex: 1, paddingLeft: '20px', minWidth: 0 },
  verticalDivider: { width: '1px', alignSelf: 'stretch', backgroundColor: 'var(--color-border)', flexShrink: 0 },
  sectionLabel: {
    fontSize: '13px', color: 'var(--color-text-secondary)',
    fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.1em', marginBottom: '10px',
  },

  statusActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  actionBtn: {
    padding: '8px 14px', borderRadius: 'var(--radius-full)', cursor: 'pointer',
    fontSize: '14px', fontWeight: 700,
    border: '1px solid transparent', transition: 'all var(--transition-fast)',
  },
  applyBtn: { backgroundColor: 'var(--color-sky-100)', color: 'var(--color-sky-600)', border: '1px solid var(--color-sky-100)' },
  interviewBtn: { backgroundColor: 'var(--color-purple-100)', color: 'var(--color-purple-600)', border: '1px solid var(--color-purple-100)' },
  offerBtn: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid var(--color-success-border)' },
  rejectBtn: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid var(--color-error-border)' },

  statusLabel: {
    padding: '8px 14px', fontSize: '14px', fontWeight: 800,
    color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface-dark)',
    borderRadius: 'var(--radius-full)', border: '1px solid var(--color-border)',
  },
  statusLabelOffer: { color: 'var(--color-success)', backgroundColor: 'var(--color-success-bg)' },
  statusLabelReject: { color: 'var(--color-error)', backgroundColor: 'var(--color-error-bg)' },

  artifactsList: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  downloadBtn: {
    padding: '8px 14px', borderRadius: 'var(--radius-full)', cursor: 'pointer',
    fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center',
    transition: 'all var(--transition-fast)',
  },
  downloadPdf: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid var(--color-error-border)' },
  downloadDoc: { backgroundColor: 'var(--color-sky-100)', color: 'var(--color-sky-600)', border: '1px solid var(--color-sky-100)' },
  pdfBtnSpinner: {
    display: 'inline-block',
    width: '11px',
    height: '11px',
    border: '2px solid rgba(220,38,38,0.25)',
    borderTopColor: 'var(--color-error)',
    borderRadius: '50%',
    animation: 'rz-spin 0.7s linear infinite',
    flexShrink: 0,
  },

  notesContainer: { marginTop: '16px', borderTop: '1px dashed var(--color-border)', paddingTop: '16px' },
  claimedSkillsBlock: {
    marginBottom: '12px', padding: '12px',
    backgroundColor: 'var(--color-surface-dark)', border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
  },
  claimedSkillsSummary: { fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px', fontWeight: 600, letterSpacing: '0.01em' },
  claimedSkillsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  claimedSkillItem: { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', backgroundColor: 'var(--color-surface)' },
  claimedSkillHeader: { fontSize: '15px', color: 'var(--color-text-primary)', fontWeight: 600 },
  claimedSkillMeta: { marginTop: '4px', fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: '1.4' },
  addNoteBtn: {
    background: 'none', border: '1px dashed var(--color-border-strong)',
    color: 'var(--color-text-muted)', fontSize: '15px',
    fontWeight: 600, cursor: 'pointer', padding: '10px 16px',
    borderRadius: 'var(--radius-md)', width: '100%', textAlign: 'center',
    transition: 'all var(--transition-fast)',
  },
  notesDisplay: {
    padding: '12px 14px',
    backgroundColor: 'var(--color-surface)',
    borderLeft: '3px solid var(--color-accent-ferrari)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)',
    cursor: 'pointer', whiteSpace: 'pre-wrap',
    lineHeight: '1.5',
  },
  noteTitle: {
    fontSize: '13px', textTransform: 'uppercase',
    fontWeight: 800, color: 'var(--color-accent-ferrari)', marginBottom: '6px',
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  notesInputActive: {
    width: '100%', minHeight: '80px', padding: '10px',
    borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-ferrari)',
    fontSize: 'var(--text-xs)', resize: 'vertical', boxSizing: 'border-box',
    outline: 'none', backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)',
    boxShadow: '0 0 8px var(--color-accent-glow)',
  },

  editForm: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' },
  inlineInput: {
    padding: '8px 10px', borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--text-xs)', width: '100%', boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)', outline: 'none',
    transition: 'border-color var(--transition-base)',
  },
  editActions: { display: 'flex', gap: '8px', marginTop: '4px' },
  saveBtn: {
    padding: '8px 14px', backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff', border: 'none',
    borderRadius: 'var(--radius-full)', cursor: 'pointer',
    fontSize: '15px', fontWeight: 700,
    transition: 'all var(--transition-fast)',
  },
  cancelBtn: {
    padding: '8px 14px', backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-full)', cursor: 'pointer',
    fontSize: '15px', fontWeight: 600,
  },

  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    animation: 'rz-fadeInFlat 0.25s ease-out',
  },
  modalContent: {
    backgroundColor: '#ffffff', borderRadius: 'var(--radius-xl)',
    padding: '28px', width: '90%', maxWidth: '700px', maxHeight: '80vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: 'var(--shadow-xl)', border: '1px solid var(--color-border)',
    animation: 'rz-slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid var(--color-border)', paddingBottom: '12px', marginBottom: '12px',
  },
  modalSubHeader: { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: '16px', fontWeight: 500 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '24px',
    cursor: 'pointer', color: 'var(--color-text-muted)', lineHeight: 1,
    transition: 'color var(--transition-fast)',
  },
  modalBody: {
    overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)', lineHeight: '1.6',
    paddingRight: '6px', backgroundColor: 'var(--color-surface-dark)',
    padding: '16px', borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  }
};

const HistoryTourPopover = ({ step, targetRef, onSkip }) => {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    const updateCoords = () => {
      if (targetRef?.current) {
        const rect = targetRef.current.getBoundingClientRect();
        setCoords({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          bottom: rect.bottom,
          right: rect.right
        });
      }
    };
    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords);
    const timer = setInterval(updateCoords, 100);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords);
      clearInterval(timer);
    };
  }, [targetRef]);

  if (!coords) return null;

  let messageText = "";
  let hintText = "";
  if (step === 1) {
    messageText = "👋 No more Excel headaches.\n\nI'll help you track every application in one place. Let's start by clicking 'Mark as Applied'!";
    hintText = "Click 'Mark as Applied'!";
  } else if (step === 2) {
    messageText = "💬 Next up!\n\nOnce they reach out for an interview, click 'Got Interview' to log your progress.";
    hintText = "Click 'Got Interview'!";
  } else if (step === 3) {
    messageText = "🎉 Final step!\n\nClick 'Got Offer!' to secure your new role and finish the tour!";
    hintText = "Click 'Got Offer!'!";
  } else if (step === 4) {
    messageText = "💡 More than a tracker!\n\nYou can click the company name to edit details, view the original JD, or add Personal Notes below to prepare for interviews.";
    hintText = "";
  }

  const cardWidth = typeof window !== 'undefined' ? (window.innerWidth <= 480 ? 260 : (window.innerWidth <= 640 ? 280 : 320)) : 320;
  const halfWidth = cardWidth / 2;
  let centerLeft = coords.left + coords.width / 2;
  const padding = 16;
  if (typeof window !== 'undefined') {
    if (centerLeft - halfWidth < padding) {
      centerLeft = halfWidth + padding;
    } else if (centerLeft + halfWidth > window.innerWidth - padding) {
      centerLeft = window.innerWidth - halfWidth - padding;
    }
  }

  return (
    <div
      className="tour-popover-card step-history"
      style={{
        position: 'fixed',
        top: `${coords.top - 12}px`,
        left: `${centerLeft}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 10002,
      }}
    >
      <div className="tour-popover-content">
        <div className="tour-popover-body">
          <p className="tour-popover-message" style={{ whiteSpace: 'pre-line' }}>
            {messageText}
          </p>
        </div>
        <div className="tour-popover-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="tour-popover-left-group">
            <div className="tour-popover-dots">
              <span className={step === 1 ? 'dot active' : 'dot'} />
              <span className={step === 2 ? 'dot active' : 'dot'} />
              <span className={step === 3 ? 'dot active' : 'dot'} />
              <span className={step === 4 ? 'dot active' : 'dot'} />
            </div>
            {step !== 4 && (
              <button className="tour-popover-skip" onClick={onSkip}>
                Skip
              </button>
            )}
          </div>
          {step === 4 ? (
            <button
              className="tour-popover-next-btn"
              onClick={onSkip}
              style={{
                padding: '6px 14px',
                backgroundColor: 'var(--color-accent-ferrari)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              Finish Tour
            </button>
          ) : (
            <span className="tour-popover-hint">{hintText}</span>
          )}
        </div>
      </div>
      <div className="tour-popover-arrow" style={{ bottom: '-8px', borderTop: '8px solid rgba(255, 255, 255, 0.88)' }} />
    </div>
  );
};

export default HistoryTable;

